import React, { memo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type SortingState,
} from '@tanstack/react-table';

interface Props<T> {
  data: T[];
  columns: Parameters<typeof useReactTable<T>>[0]['columns'];
  isLoading?: boolean;
  isRefetching?: boolean;
  loadingMessage?: string;
}

function DataTableImpl<T>({
  data,
  columns,
  isLoading,
  isRefetching,
  loadingMessage = 'Loading...',
}: Props<T>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = React.useState('');

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (isLoading) {
    return (
      <div className="admin-table-container">
        <div className="admin-loading">{loadingMessage}</div>
      </div>
    );
  }

  const rowCount = table.getFilteredRowModel().rows.length;

  return (
    <div className="admin-table-container">
      <div className="admin-table-toolbar">
        <input
          type="text"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
          className="admin-search-input"
        />
        <span className="admin-table-footer">
          {isRefetching && <span className="admin-refetch-indicator" />}
          {rowCount} {rowCount === 1 ? 'row' : 'rows'}
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="admin-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isSorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className={isSorted ? 'sorted' : ''}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {isSorted === 'asc' && ' \u2191'}
                      {isSorted === 'desc' && ' \u2193'}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: 'center', padding: '48px' }}
                >
                  <div className="admin-empty">
                    <div className="admin-empty-title">No data found</div>
                    <div className="admin-empty-description">
                      {globalFilter
                        ? 'Try adjusting your search'
                        : 'No records to display'}
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const header = cell.column.columnDef.header;
                    const label = typeof header === 'string' ? header : cell.column.id;
                    return (
                      <td key={cell.id}>
                        <span className="admin-td-label">{label}</span>
                        <div className="admin-td-value">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const DataTable = memo(DataTableImpl) as typeof DataTableImpl;
